import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, KRI } from '../lib/api'
import { useDatePreferences } from '../hooks/useDatePreferences'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts'
import {
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Activity,
  Calendar,
  TrendingUp,
  TrendingDown,
  Info,
  RefreshCw,
  Briefcase,
  PieChart,
  ChevronDown,
  ChevronRight,
  X,
  Lightbulb,
  Target,
  Gauge,
  AlertCircle,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface DashboardData {
  compliance_score: number
  controls: {
    total: number
    applicable: number
    not_applicable: number
    compliant: number
    non_compliant: number
    in_progress: number
    pending: number
    overdue: number
  }
  evidence: {
    total: number
    last_30_days: number
    last_7_days: number
  }
  risks: {
    total: number
    open: number
    critical_open: number
    high_open: number
  }
  frameworks: Array<{
    id: string
    display_name: string
    total_controls: number
    applicable_controls: number
    not_applicable_controls: number
    compliant_controls: number
    compliance_percentage: number
  }>
  upcoming_tasks: Array<{
    type: string
    id: string
    title: string
    description: string
    due_date: number
  }>
}

interface ActivityItem {
  type: string
  id: string
  title: string
  control_number?: string
  username?: string
  status?: string
  timestamp: number
}

interface TrendDataPoint {
  date: string
  count: number
}

interface BenchmarkData {
  framework: string
  display_name: string
  your_score: number
  industry_average: number
  top_quartile: number
  vs_average: number
  vs_top: number
}

type DateRangePreset = '7d' | '30d' | '90d' | 'custom'

interface ScoreBreakdownData {
  overall: {
    compliance_score: number
    formula: string
    methodology: string
    total_controls: number
    applicable_controls: number
    compliant_controls: number
    non_compliant_controls: number
    in_progress_controls: number
    pending_controls: number
    not_applicable_controls: number
    overdue_controls: number
  }
  frameworks: Array<{
    id: string
    name: string
    code: string
    total_controls: number
    compliant_controls: number
    non_compliant_controls: number
    in_progress_controls: number
    pending_controls: number
    not_applicable_controls: number
    overdue_controls: number
    compliance_score: number
    controls: Array<{
      id: string
      control_number: string
      name: string
      status: string
      status_reason: string
      frequency: string
      is_overdue: boolean
      days_until_due: number | null
      owner_username: string | null
      impact_score: number
      contribution: number
    }>
  }>
  recommendations: {
    summary: string
    items: Array<{
      control_id: string
      control_number: string
      control_name: string
      framework_name: string
      current_status: string
      priority: 'critical' | 'high' | 'medium' | 'low'
      action: string
      impact: string
      days_until_due: number | null
      owner: string
    }>
  }
  status_definitions: {
    compliant: string
    non_compliant: string
    in_progress: string
    pending: string
    not_applicable: string
  }
  generated_at: number
}

// ============================================================================
// DATE RANGE PICKER COMPONENT
// ============================================================================

interface DateRangePickerProps {
  selectedPreset: DateRangePreset
  customStartDate: string
  customEndDate: string
  onPresetChange: (preset: DateRangePreset) => void
  onCustomDateChange: (start: string, end: string) => void
}

function DateRangePicker({
  selectedPreset,
  customStartDate,
  customEndDate,
  onPresetChange,
  onCustomDateChange,
}: DateRangePickerProps) {
  const [showCustom, setShowCustom] = useState(selectedPreset === 'custom')

  return (
    <div className="flex items-center gap-2">
      <div className="flex bg-gray-100 rounded-lg p-1">
        {(['7d', '30d', '90d'] as const).map((preset) => (
          <button
            key={preset}
            onClick={() => {
              onPresetChange(preset)
              setShowCustom(false)
            }}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              selectedPreset === preset && !showCustom
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {preset === '7d' ? '7 Days' : preset === '30d' ? '30 Days' : '90 Days'}
          </button>
        ))}
        <button
          onClick={() => {
            setShowCustom(true)
            onPresetChange('custom')
          }}
          className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
            showCustom
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Calendar className="h-3 w-3" />
          Custom
        </button>
      </div>
      {showCustom && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={customStartDate}
            onChange={(e) => onCustomDateChange(e.target.value, customEndDate)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={customEndDate}
            onChange={(e) => onCustomDateChange(customStartDate, e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          />
        </div>
      )}
    </div>
  )
}

// ============================================================================
// TOOLTIP COMPONENT
// ============================================================================

interface TooltipIconProps {
  content: string
}

function TooltipIcon({ content }: TooltipIconProps) {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="More information"
      >
        <Info className="h-4 w-4" />
      </button>
      {isVisible && (
        <div className="absolute z-50 w-64 p-3 text-sm text-gray-700 bg-white rounded-lg shadow-lg border border-gray-200 bottom-full left-1/2 -translate-x-1/2 mb-2">
          <div className="absolute w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45 left-1/2 -translate-x-1/2 -bottom-1.5" />
          {content}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// AUTO-REFRESH INDICATOR
// ============================================================================

interface AutoRefreshIndicatorProps {
  lastUpdated: Date | null
  isRefreshing: boolean
  onManualRefresh: () => void
  autoRefreshEnabled: boolean
  onToggleAutoRefresh: () => void
}

function AutoRefreshIndicator({
  lastUpdated,
  isRefreshing,
  onManualRefresh,
  autoRefreshEnabled,
  onToggleAutoRefresh,
}: AutoRefreshIndicatorProps) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-500">
      {lastUpdated && (
        <span>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </span>
      )}
      <button
        onClick={onManualRefresh}
        disabled={isRefreshing}
        className="flex items-center gap-1 text-primary-600 hover:text-primary-700 disabled:opacity-50"
      >
        <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        {isRefreshing ? 'Refreshing...' : 'Refresh'}
      </button>
      <label className="flex items-center gap-1 cursor-pointer">
        <input
          type="checkbox"
          checked={autoRefreshEnabled}
          onChange={onToggleAutoRefresh}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span>Auto-refresh (30s)</span>
      </label>
    </div>
  )
}

// ============================================================================
// SCORE BREAKDOWN MODAL COMPONENT
// ============================================================================

interface ScoreBreakdownModalProps {
  isOpen: boolean
  onClose: () => void
  data: ScoreBreakdownData | null
  loading: boolean
  onNavigateToControl: (controlId: string) => void
}

function ScoreBreakdownModal({ isOpen, onClose, data, loading, onNavigateToControl }: ScoreBreakdownModalProps) {
  const { formatDateTime } = useDatePreferences()
  const [expandedFrameworks, setExpandedFrameworks] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<'overview' | 'frameworks' | 'recommendations'>('overview')

  if (!isOpen) return null

  const toggleFramework = (frameworkId: string) => {
    const newExpanded = new Set(expandedFrameworks)
    if (newExpanded.has(frameworkId)) {
      newExpanded.delete(frameworkId)
    } else {
      newExpanded.add(frameworkId)
    }
    setExpandedFrameworks(newExpanded)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800'
      case 'non_compliant':
        return 'bg-red-100 text-red-800'
      case 'in_progress':
        return 'bg-blue-100 text-blue-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'not_applicable':
        return 'bg-gray-100 text-gray-600'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200'
    }
  }

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <PieChart className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Compliance Score Breakdown</h2>
              <p className="text-sm text-gray-500">Detailed analysis of your compliance score calculation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('frameworks')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'frameworks'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Framework Details
          </button>
          <button
            onClick={() => setActiveTab('recommendations')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'recommendations'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Recommendations
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : data ? (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Score Summary */}
                  <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Overall Compliance Score</h3>
                        <p className="text-5xl font-bold text-primary-600 mt-2">{data.overall.compliance_score}%</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-green-600">{data.overall.compliant_controls}</span> compliant of{' '}
                          <span className="font-medium">{data.overall.applicable_controls}</span> applicable controls
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          ({data.overall.not_applicable_controls} marked N/A)
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Formula Explanation */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">How is the score calculated?</h4>
                        <p className="text-sm text-blue-800 mt-1 font-mono bg-blue-100 px-2 py-1 rounded inline-block">
                          {data.overall.formula}
                        </p>
                        <p className="text-sm text-blue-700 mt-2">{data.overall.methodology}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Breakdown */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{data.overall.compliant_controls}</div>
                      <div className="text-sm text-green-700">Compliant</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-600">{data.overall.non_compliant_controls}</div>
                      <div className="text-sm text-red-700">Non-Compliant</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{data.overall.in_progress_controls}</div>
                      <div className="text-sm text-blue-700">In Progress</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">{data.overall.pending_controls}</div>
                      <div className="text-sm text-yellow-700">Pending</div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">{data.overall.overdue_controls}</div>
                      <div className="text-sm text-orange-700">Overdue</div>
                    </div>
                  </div>

                  {/* Status Definitions */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Status Definitions</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {Object.entries(data.status_definitions).map(([status, definition]) => (
                        <div key={status} className="flex items-start gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(status)}`}>
                            {formatStatus(status)}
                          </span>
                          <span className="text-gray-600">{definition}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Frameworks Tab */}
              {activeTab === 'frameworks' && (
                <div className="space-y-4">
                  {data.frameworks.map((framework) => (
                    <div key={framework.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Framework Header */}
                      <button
                        onClick={() => toggleFramework(framework.id)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {expandedFrameworks.has(framework.id) ? (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                          <div className="text-left">
                            <h4 className="font-medium text-gray-900">{framework.name}</h4>
                            <p className="text-sm text-gray-500">
                              {framework.compliant_controls} of {framework.total_controls - framework.not_applicable_controls} applicable controls compliant
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${
                              framework.compliance_score >= 80 ? 'text-green-600' :
                              framework.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {framework.compliance_score}%
                            </div>
                          </div>
                          {/* Mini status indicators */}
                          <div className="flex gap-1">
                            {framework.overdue_controls > 0 && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                {framework.overdue_controls} overdue
                              </span>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Framework Controls */}
                      {expandedFrameworks.has(framework.id) && (
                        <div className="border-t border-gray-200">
                          <div className="max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600">Control</th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600">Contribution</th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600">Due</th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-600">Owner</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {framework.controls.map((control) => (
                                  <tr
                                    key={control.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => {
                                      onClose()
                                      onNavigateToControl(control.id)
                                    }}
                                  >
                                    <td className="px-4 py-3">
                                      <div className="font-medium text-gray-900">{control.control_number}</div>
                                      <div className="text-gray-500 text-xs truncate max-w-xs">{control.name}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(control.status)}`}>
                                        {formatStatus(control.status)}
                                      </span>
                                      {control.is_overdue && (
                                        <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                          Overdue
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      {control.contribution > 0 ? (
                                        <span className={`font-medium ${control.status === 'compliant' ? 'text-green-600' : 'text-gray-600'}`}>
                                          {control.status === 'compliant' ? '+' : ''}{control.contribution.toFixed(1)}%
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">N/A</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                      {control.days_until_due !== null ? (
                                        control.days_until_due < 0 ? (
                                          <span className="text-red-600">{Math.abs(control.days_until_due)}d overdue</span>
                                        ) : control.days_until_due === 0 ? (
                                          <span className="text-yellow-600">Today</span>
                                        ) : (
                                          <span>{control.days_until_due}d</span>
                                        )
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                      {control.owner_username || <span className="text-gray-400">Unassigned</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations Tab */}
              {activeTab === 'recommendations' && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-900">Improvement Recommendations</h4>
                        <p className="text-sm text-amber-700 mt-1">{data.recommendations.summary}</p>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations List */}
                  {data.recommendations.items.length > 0 ? (
                    <div className="space-y-3">
                      {data.recommendations.items.map((rec, index) => (
                        <div
                          key={rec.control_id}
                          className={`border rounded-lg p-4 ${getPriorityColor(rec.priority)} cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => {
                            onClose()
                            onNavigateToControl(rec.control_id)
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full text-sm font-bold">
                                {index + 1}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{rec.control_number}</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                                    rec.priority === 'critical' ? 'bg-red-200 text-red-800' :
                                    rec.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                                    rec.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                                    'bg-blue-200 text-blue-800'
                                  }`}>
                                    {rec.priority}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1">{rec.control_name}</p>
                                <p className="text-xs text-gray-500 mt-1">{rec.framework_name}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-1 text-green-600 font-medium">
                                <Target className="h-4 w-4" />
                                {rec.impact}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between text-sm">
                            <div className="flex items-center gap-4">
                              <span className="text-gray-600">
                                <span className="font-medium">Action:</span> {rec.action}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-500">
                              <span>Owner: {rec.owner}</span>
                              {rec.days_until_due !== null && (
                                <span className={rec.days_until_due < 0 ? 'text-red-600' : ''}>
                                  {rec.days_until_due < 0
                                    ? `${Math.abs(rec.days_until_due)}d overdue`
                                    : `Due in ${rec.days_until_due}d`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <h4 className="text-lg font-medium text-gray-900">All Controls Compliant</h4>
                      <p className="text-gray-500 mt-1">Great job! All applicable controls are in compliance.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Failed to load score breakdown data
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="text-xs text-gray-500">
            {data && `Generated at ${formatDateTime(data.generated_at)}`}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// EXECUTIVE SUMMARY COMPONENT
// ============================================================================

interface ExecutiveSummaryProps {
  data: DashboardData
  benchmarks: BenchmarkData[]
  onNavigate: (path: string) => void
}

function ExecutiveSummary({ data, benchmarks, onNavigate }: ExecutiveSummaryProps) {
  const { formatLongDate } = useDatePreferences()
  const complianceScore = data.compliance_score || 0
  const avgBenchmark = benchmarks.length > 0
    ? Math.round(benchmarks.reduce((sum, b) => sum + b.industry_average, 0) / benchmarks.length)
    : 70
  const vsBenchmark = complianceScore - avgBenchmark

  // Calculate risk score (inverse - lower is better)
  const totalRisks = data.risks.total || 1
  const openRisks = data.risks.open || 0
  const riskScore = Math.round(100 - (openRisks / totalRisks) * 100)

  // Evidence freshness score
  const totalEvidence = data.evidence.total || 1
  const recentEvidence = data.evidence.last_30_days || 0
  const evidenceFreshness = Math.round((recentEvidence / totalEvidence) * 100)

  return (
    <div className="bg-gradient-to-br from-primary-700 via-primary-600 to-primary-500 rounded-xl p-6 text-white shadow-elevated relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
      <div className="flex items-center justify-between mb-4 relative">
        <h2 className="text-lg font-heading font-semibold tracking-snug">Executive Summary</h2>
        <span className="text-primary-200 text-sm">
          {formatLongDate(new Date())}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
        {/* Overall Compliance */}
        <button
          onClick={() => onNavigate('/controls?status=compliant')}
          className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-colors group"
        >
          <div className="text-4xl font-heading font-bold mb-1 group-hover:underline tracking-display">{complianceScore}%</div>
          <div className="text-primary-200 text-sm">Overall Compliance</div>
          <div className={`flex items-center justify-center mt-1 text-sm ${vsBenchmark >= 0 ? 'text-green-300' : 'text-yellow-300'}`}>
            {vsBenchmark >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
            {vsBenchmark >= 0 ? '+' : ''}{vsBenchmark}% vs industry
          </div>
        </button>

        {/* Controls Status */}
        <button
          onClick={() => onNavigate('/controls')}
          className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-colors group"
        >
          <div className="text-4xl font-heading font-bold mb-1 group-hover:underline tracking-display">{data.controls.compliant}/{data.controls.applicable}</div>
          <div className="text-primary-200 text-sm">Controls Compliant</div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onNavigate('/controls?status=overdue')
            }}
            className="flex items-center justify-center mt-1 text-sm text-yellow-300 hover:underline"
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            {data.controls.overdue} overdue
          </button>
        </button>

        {/* Risk Posture */}
        <button
          onClick={() => onNavigate('/risks?status=open')}
          className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-colors group"
        >
          <div className="text-4xl font-heading font-bold mb-1 group-hover:underline tracking-display">{riskScore}%</div>
          <div className="text-primary-200 text-sm">Risk Posture</div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onNavigate('/risks?status=open&severity=critical')
            }}
            className="flex items-center justify-center mt-1 text-sm text-red-300 hover:underline"
          >
            <XCircle className="h-4 w-4 mr-1" />
            {data.risks.critical_open} critical open
          </button>
        </button>

        {/* Evidence Activity */}
        <button
          onClick={() => onNavigate('/evidence')}
          className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-colors group"
        >
          <div className="text-4xl font-heading font-bold mb-1 group-hover:underline tracking-display">{data.evidence.last_7_days}</div>
          <div className="text-primary-200 text-sm">Evidence This Week</div>
          <div className="flex items-center justify-center mt-1 text-sm text-green-300">
            <TrendingUp className="h-4 w-4 mr-1" />
            {evidenceFreshness}% fresh evidence
          </div>
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// TREND CHART COMPONENT
// ============================================================================

interface TrendChartProps {
  data: TrendDataPoint[]
  title: string
  color: string
  loading?: boolean
}

function TrendChart({ data, title, color, loading }: TrendChartProps) {
  const { formatChartDate } = useDatePreferences()

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        No trend data available for this period
      </div>
    )
  }

  // Format data for chart - using user's locale preferences
  const formattedData = data.map((d) => ({
    ...d,
    displayDate: formatChartDate(new Date(d.date)),
  }))

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={formattedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            }}
            labelStyle={{ fontWeight: 600 }}
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke={color}
            strokeWidth={2}
            fill={`url(#gradient-${color})`}
            name={title}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { formatDate, formatDateTime, formatRelativeTime: formatRelative, formatChartDate, formatLongDate } = useDatePreferences()

  // Core data state
  const [data, setData] = useState<DashboardData | null>(null)
  const [alerts, setAlerts] = useState<any[]>([])
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [trends, setTrends] = useState<TrendDataPoint[]>([])
  const [benchmarks, setBenchmarks] = useState<BenchmarkData[]>([])
  const [riskReductionSummary, setRiskReductionSummary] = useState<{
    total_risks: number
    risks_with_reduction_data: number
    overall_reduction_percentage: number
    average_effectiveness_rating: number | null
    reduction_distribution: { significant: number; moderate: number; low: number }
    total_risk_reduced: number
  } | null>(null)
  const [expiringDocs, setExpiringDocs] = useState<{
    expired: Array<{ id: string; title: string; category: string; expires_at: number; days_until_expiration: number }>
    expiring_soon: Array<{ id: string; title: string; category: string; expires_at: number; days_until_expiration: number }>
    expired_count: number
    expiring_soon_count: number
  }>({ expired: [], expiring_soon: [], expired_count: 0, expiring_soon_count: 0 })

  // Loading and error state
  const [loading, setLoading] = useState(true)
  const [trendsLoading, setTrendsLoading] = useState(true)
  const [error, setError] = useState('')

  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Score breakdown modal state
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false)
  const [scoreBreakdownData, setScoreBreakdownData] = useState<ScoreBreakdownData | null>(null)
  const [scoreBreakdownLoading, setScoreBreakdownLoading] = useState(false)

  // KRI state (Issue #127)
  const [kriSummary, setKriSummary] = useState<{
    total: number
    green: number
    yellow: number
    red: number
    no_data: number
  } | null>(null)
  const [topKris, setTopKris] = useState<KRI[]>([])

  // Date range state - initialize from URL params
  const getInitialPreset = (): DateRangePreset => {
    const preset = searchParams.get('preset')
    if (preset === '7d' || preset === '30d' || preset === '90d' || preset === 'custom') {
      return preset
    }
    return '30d'
  }

  const getInitialCustomDates = () => {
    const start = searchParams.get('start')
    const end = searchParams.get('end')
    return {
      start: start || '',
      end: end || ''
    }
  }

  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(getInitialPreset)
  const [customStartDate, setCustomStartDate] = useState(getInitialCustomDates().start)
  const [customEndDate, setCustomEndDate] = useState(getInitialCustomDates().end)

  // Compute date range timestamps based on preset or custom dates
  const dateRange = useMemo(() => {
    const now = Date.now()
    if (dateRangePreset === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: new Date(customStartDate).getTime(),
        endDate: new Date(customEndDate).setHours(23, 59, 59, 999)
      }
    }

    const days = dateRangePreset === '7d' ? 7 : dateRangePreset === '90d' ? 90 : 30
    return {
      startDate: now - (days * 24 * 60 * 60 * 1000),
      endDate: now
    }
  }, [dateRangePreset, customStartDate, customEndDate])

  // Helper functions - use preferences-aware formatRelative from useDatePreferences
  const formatRelativeTime = (timestamp: number) => {
    return formatRelative(timestamp)
  }

  const getDaysUntilDue = (dueDate: number) => {
    const now = Date.now()
    const diff = dueDate - now
    const days = Math.ceil(diff / 86400000)
    return days
  }

  const getDueUrgencyClass = (days: number) => {
    if (days < 0) return 'text-red-600 bg-red-50'
    if (days <= 3) return 'text-red-600 bg-red-50'
    if (days <= 7) return 'text-yellow-600 bg-yellow-50'
    return 'text-gray-600 bg-gray-50'
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'evidence_uploaded':
        return <FileText className="h-4 w-4 text-green-500" />
      case 'control_updated':
        return <Shield className="h-4 w-4 text-blue-500" />
      default:
        return <Activity className="h-4 w-4 text-gray-500" />
    }
  }

  const getActivityDescription = (item: ActivityItem) => {
    switch (item.type) {
      case 'evidence_uploaded':
        return `uploaded evidence for ${item.control_number || 'a control'}`
      case 'control_updated':
        return `updated ${item.control_number || 'control'} to ${item.status || 'new status'}`
      default:
        return item.title || 'performed an action'
    }
  }

  // Fetch score breakdown data
  const fetchScoreBreakdown = useCallback(async () => {
    setScoreBreakdownLoading(true)
    try {
      const response = await api.getScoreBreakdown()
      if (response.success && response.score_breakdown) {
        setScoreBreakdownData(response.score_breakdown)
      }
    } catch (err) {
      console.error('Failed to fetch score breakdown:', err)
    } finally {
      setScoreBreakdownLoading(false)
    }
  }, [])

  // Handle opening score breakdown modal
  const handleOpenScoreBreakdown = useCallback(() => {
    setShowScoreBreakdown(true)
    if (!scoreBreakdownData) {
      fetchScoreBreakdown()
    }
  }, [scoreBreakdownData, fetchScoreBreakdown])

  // Navigate to specific control
  const navigateToControl = useCallback((controlId: string) => {
    navigate(`/controls?id=${controlId}`)
  }, [navigate])

  // Calculate days from date range preset
  const getDaysFromPreset = useCallback(() => {
    switch (dateRangePreset) {
      case '7d': return 7
      case '30d': return 30
      case '90d': return 90
      case 'custom':
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate).getTime()
          const end = new Date(customEndDate).getTime()
          return Math.ceil((end - start) / 86400000)
        }
        return 30
      default: return 30
    }
  }, [dateRangePreset, customStartDate, customEndDate])

  // Fetch trends data
  const fetchTrends = useCallback(async () => {
    setTrendsLoading(true)
    try {
      const days = getDaysFromPreset()

      const [trendsRes, benchmarksRes, riskReductionRes] = await Promise.all([
        api.getDashboardTrends(days, dateRange.startDate, dateRange.endDate),
        api.getDashboardBenchmarks(),
        api.getRiskReductionSummary(),
      ])

      if (trendsRes.success) {
        setTrends(trendsRes.trends.evidence_collection)
      }
      if (benchmarksRes.success) {
        setBenchmarks(benchmarksRes.benchmarks)
      }
      if (riskReductionRes.success) {
        setRiskReductionSummary(riskReductionRes.summary)
      }
    } catch (err) {
      console.error('Failed to load trends:', err)
    } finally {
      setTrendsLoading(false)
    }
  }, [getDaysFromPreset, dateRange.startDate, dateRange.endDate])

  // Core data fetch - now includes date range filtering
  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setIsRefreshing(true)

    try {
      const [overviewRes, alertsRes, activityRes, expiringDocsRes, kriRes] = await Promise.all([
        api.getDashboardOverview(dateRange.startDate, dateRange.endDate),
        api.getDashboardAlerts(),
        api.getDashboardActivity(10, dateRange.startDate, dateRange.endDate),
        api.getExpiringDocuments(30),
        api.getKRIDashboard().catch(() => ({ success: false })), // KRI dashboard may not exist yet
      ])

      if (overviewRes.success) {
        setData(overviewRes.overview)
      }
      if (alertsRes.success) {
        setAlerts(alertsRes.alerts.slice(0, 5))
      }
      if (activityRes.success) {
        setActivity(activityRes.activity.slice(0, 8))
      }
      if (expiringDocsRes.success) {
        setExpiringDocs({
          expired: expiringDocsRes.expired,
          expiring_soon: expiringDocsRes.expiring_soon,
          expired_count: expiringDocsRes.expired_count,
          expiring_soon_count: expiringDocsRes.expiring_soon_count,
        })
      }
      // KRI data (Issue #127)
      if (kriRes.success && 'dashboard' in kriRes) {
        setKriSummary(kriRes.dashboard.summary)
        // Show top KRIs that are not green (yellow/red) or no data, limited to 4
        const nonGreenKris = kriRes.dashboard.kris.filter(
          (k: KRI) => k.current_status !== 'green'
        ).slice(0, 4)
        setTopKris(nonGreenKris)
      }
      setLastUpdated(new Date())
    } catch (err) {
      if (!isRefresh) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      }
    } finally {
      if (isRefresh) setIsRefreshing(false)
      setLoading(false)
    }
  }, [dateRange.startDate, dateRange.endDate])

  // Update URL params when date range changes
  useEffect(() => {
    const newParams = new URLSearchParams()
    newParams.set('preset', dateRangePreset)
    if (dateRangePreset === 'custom' && customStartDate && customEndDate) {
      newParams.set('start', customStartDate)
      newParams.set('end', customEndDate)
    }
    setSearchParams(newParams, { replace: true })
  }, [dateRangePreset, customStartDate, customEndDate, setSearchParams])

  // Initial load and refresh when date range changes
  useEffect(() => {
    fetchData()
    fetchTrends()
  }, [fetchData, fetchTrends])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefreshEnabled) return

    const interval = setInterval(() => {
      fetchData(true)
    }, 30000)

    return () => clearInterval(interval)
  }, [autoRefreshEnabled, fetchData])

  // Handle date range changes
  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset)
  }

  const handleCustomDateChange = (start: string, end: string) => {
    setCustomStartDate(start)
    setCustomEndDate(end)
  }

  const handleManualRefresh = () => {
    fetchData(true)
    fetchTrends()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    )
  }

  const scoreColor =
    (data?.compliance_score || 0) >= 80
      ? 'text-green-600'
      : (data?.compliance_score || 0) >= 60
      ? 'text-yellow-600'
      : 'text-red-600'

  // Format date range for display - uses user's locale preferences
  const formatDateRangeLabel = () => {
    if (dateRangePreset === 'custom' && customStartDate && customEndDate) {
      const start = formatDate(new Date(customStartDate), { month: 'short', day: 'numeric' })
      const end = formatDate(new Date(customEndDate), { month: 'short', day: 'numeric', year: 'numeric' })
      return `${start} - ${end}`
    }
    const days = dateRangePreset === '7d' ? 7 : dateRangePreset === '90d' ? 90 : 30
    return `Last ${days} days`
  }

  return (
    <div className="space-y-6">
      {/* Header with Date Range and Auto-Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900 tracking-display">Dashboard</h1>
          <p className="text-gray-500 mt-0.5">
            Compliance overview and metrics <span className="text-primary-600 font-medium">({formatDateRangeLabel()})</span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Link
            to="/dashboard/executive"
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Briefcase className="h-4 w-4" />
            Executive View
          </Link>
          <DateRangePicker
            selectedPreset={dateRangePreset}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onPresetChange={handlePresetChange}
            onCustomDateChange={handleCustomDateChange}
          />
          <AutoRefreshIndicator
            lastUpdated={lastUpdated}
            isRefreshing={isRefreshing}
            onManualRefresh={handleManualRefresh}
            autoRefreshEnabled={autoRefreshEnabled}
            onToggleAutoRefresh={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
          />
        </div>
      </div>

      {/* Executive Summary */}
      {data && <ExecutiveSummary data={data} benchmarks={benchmarks} onNavigate={navigate} />}

      {/* Compliance Score with Tooltip */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-gray-500">
              Overall Compliance Score
            </h2>
            <TooltipIcon
              content="Compliance score is calculated as: (Compliant Controls / Total Controls) x 100. Only controls from enabled frameworks are included. A control is marked compliant when it has current evidence and meets all requirements."
            />
          </div>
          <div className="flex items-center justify-center">
            <div className="relative">
              <svg className="w-32 h-32" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray={`${data?.compliance_score || 0}, 100`}
                  className={scoreColor}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-3xl font-heading font-bold tracking-display ${scoreColor}`}>
                  {data?.compliance_score || 0}%
                </span>
              </div>
            </div>
          </div>
          {/* View Score Breakdown Button */}
          <button
            onClick={handleOpenScoreBreakdown}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
          >
            <PieChart className="h-4 w-4" />
            View Score Breakdown
          </button>
        </div>

        {/* Stats Cards */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/controls?status=compliant')}
            className="card p-4 text-left cursor-pointer hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              <Shield className="h-8 w-8 text-primary-500" />
              <span className="badge-blue">{data?.controls.total || 0}</span>
            </div>
            <p className="mt-2 text-2xl font-heading font-bold text-gray-900 tracking-display group-hover:text-primary-600">
              {data?.controls.compliant || 0}
            </p>
            <p className="text-sm text-gray-500">Compliant Controls</p>
          </button>

          <button
            onClick={() => navigate('/evidence')}
            className="card p-4 text-left cursor-pointer hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              <FileText className="h-8 w-8 text-green-500" />
              <span className="badge-green">+{data?.evidence.last_7_days || 0}</span>
            </div>
            <p className="mt-2 text-2xl font-heading font-bold text-gray-900 tracking-display group-hover:text-green-600">
              {data?.evidence.total || 0}
            </p>
            <p className="text-sm text-gray-500">Evidence Items</p>
          </button>

          <button
            onClick={() => navigate('/controls?status=overdue')}
            className="card p-4 text-left cursor-pointer hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
              <span className="badge-yellow">{data?.controls.overdue || 0}</span>
            </div>
            <p className="mt-2 text-2xl font-heading font-bold text-gray-900 tracking-display group-hover:text-yellow-600">
              {data?.controls.overdue || 0}
            </p>
            <p className="text-sm text-gray-500">Overdue Controls</p>
          </button>

          <button
            onClick={() => navigate('/risks?status=open')}
            className="card p-4 text-left cursor-pointer hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              <XCircle className="h-8 w-8 text-red-500" />
              <span className="badge-red">{data?.risks.critical_open || 0} critical</span>
            </div>
            <p className="mt-2 text-2xl font-heading font-bold text-gray-900 tracking-display group-hover:text-red-600">
              {data?.risks.open || 0}
            </p>
            <p className="text-sm text-gray-500">Open Risks</p>
          </button>
        </div>
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-heading font-semibold text-gray-900 mb-4 flex items-center tracking-snug">
            <TrendingUp className="h-5 w-5 mr-2 text-primary-500" />
            Evidence Collection Trend
          </h2>
          <TrendChart
            data={trends}
            title="Evidence Collected"
            color="#10b981"
            loading={trendsLoading}
          />
        </div>

        {/* Benchmark Comparison */}
        <div className="card p-6">
          <h2 className="text-lg font-heading font-semibold text-gray-900 mb-4 flex items-center tracking-snug">
            <Shield className="h-5 w-5 mr-2 text-primary-500" />
            Industry Benchmark Comparison
          </h2>
          {trendsLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : benchmarks.length > 0 ? (
            <div className="space-y-4">
              {benchmarks.slice(0, 4).map((benchmark) => (
                <div key={benchmark.framework}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      {benchmark.display_name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${
                        benchmark.vs_average >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {benchmark.your_score}%
                      </span>
                      <span className="text-xs text-gray-400">
                        vs {benchmark.industry_average}% avg
                      </span>
                    </div>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    {/* Industry average marker */}
                    <div
                      className="absolute h-full w-0.5 bg-gray-400 z-10"
                      style={{ left: `${benchmark.industry_average}%` }}
                    />
                    {/* Your score bar */}
                    <div
                      className={`h-full rounded-full ${
                        benchmark.vs_average >= 0 ? 'bg-green-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${benchmark.your_score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No benchmark data available
            </div>
          )}
        </div>
      </div>

      {/* Risk Reduction Metrics Card */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-gray-900 flex items-center tracking-snug">
            <TrendingDown className="h-5 w-5 mr-2 text-green-500" />
            Risk Reduction Metrics
          </h2>
          <Link
            to="/risks"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            View Risks <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        {trendsLoading ? (
          <div className="h-32 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : riskReductionSummary && riskReductionSummary.risks_with_reduction_data > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Overall Reduction */}
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <div className={`text-3xl font-bold ${
                riskReductionSummary.overall_reduction_percentage > 50
                  ? 'text-green-600'
                  : riskReductionSummary.overall_reduction_percentage >= 25
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}>
                {riskReductionSummary.overall_reduction_percentage}%
              </div>
              <p className="text-sm text-gray-600 mt-1">Overall Reduction</p>
              <p className="text-xs text-gray-500 mt-1">
                {riskReductionSummary.total_risk_reduced} points reduced
              </p>
            </div>

            {/* Distribution */}
            <div className="p-4 bg-surface-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Reduction Distribution</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-green-600">High (&gt;50%)</span>
                  <span className="text-sm font-semibold">{riskReductionSummary.reduction_distribution.significant}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-yellow-600">Moderate (25-50%)</span>
                  <span className="text-sm font-semibold">{riskReductionSummary.reduction_distribution.moderate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-red-600">Low (&lt;25%)</span>
                  <span className="text-sm font-semibold">{riskReductionSummary.reduction_distribution.low}</span>
                </div>
              </div>
            </div>

            {/* Control Effectiveness */}
            <div className="p-4 bg-surface-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Avg Control Effectiveness</p>
              {riskReductionSummary.average_effectiveness_rating !== null ? (
                <div className="flex items-center justify-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(riskReductionSummary.average_effectiveness_rating!)
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="ml-1 text-sm font-medium text-gray-600">
                    {riskReductionSummary.average_effectiveness_rating.toFixed(1)}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center mt-2">Not rated</p>
              )}
            </div>

            {/* Coverage */}
            <div className="p-4 bg-surface-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Reduction Coverage</p>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {riskReductionSummary.risks_with_reduction_data}
                </p>
                <p className="text-xs text-gray-500">
                  of {riskReductionSummary.total_risks} risks tracked
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full"
                    style={{
                      width: `${riskReductionSummary.total_risks > 0
                        ? (riskReductionSummary.risks_with_reduction_data / riskReductionSummary.total_risks) * 100
                        : 0}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <TrendingDown className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No risk reduction data available yet.</p>
            <p className="text-xs mt-1">Set residual risk scores on your risks to track reduction.</p>
            <Link
              to="/risks"
              className="inline-flex items-center mt-3 text-sm text-primary-600 hover:text-primary-700"
            >
              Go to Risks <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        )}
      </div>

      {/* Framework Compliance & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Framework Compliance */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-gray-900 tracking-snug">
              Framework Compliance
            </h2>
            <Link
              to="/controls"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-4">
            {data?.frameworks && data.frameworks.length > 0 ? (
              data.frameworks.map((framework) => (
                <button
                  key={framework.id}
                  onClick={() => navigate(`/controls?framework_id=${framework.id}`)}
                  className="block w-full text-left cursor-pointer hover:bg-surface-100 rounded-lg p-2 -mx-2 transition-colors group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 group-hover:text-primary-600">
                      {framework.display_name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {framework.compliant_controls}/{framework.applicable_controls || framework.total_controls} controls
                    </span>
                  </div>
                  <div className="w-full bg-surface-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        framework.compliance_percentage >= 80
                          ? 'bg-emerald-500'
                          : framework.compliance_percentage >= 60
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${framework.compliance_percentage}%` }}
                    />
                  </div>
                </button>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No frameworks configured</p>
            )}
          </div>
        </div>

        {/* Alerts */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-gray-900 tracking-snug">Alerts</h2>
            <span className="badge-red">{alerts.length} active</span>
          </div>
          <div className="space-y-3">
            {alerts.length > 0 ? (
              alerts.map((alert, index) => {
                // Determine navigation path based on alert type
                const getAlertPath = () => {
                  if (alert.control_id) return `/controls/${alert.control_id}`
                  if (alert.risk_id) return `/risks?status=open`
                  return null
                }
                const alertPath = getAlertPath()

                return (
                  <button
                    key={index}
                    onClick={() => alertPath && navigate(alertPath)}
                    className={`w-full text-left p-3 rounded-lg border cursor-pointer transition-all group ${
                      alert.severity === 'critical'
                        ? 'bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300'
                        : alert.severity === 'high'
                        ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100 hover:border-yellow-300'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      {alert.severity === 'critical' ? (
                        <XCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                      ) : alert.severity === 'high' ? (
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 group-hover:underline">
                          {alert.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {alert.message}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 ml-2" />
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="text-gray-500">No active alerts</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Expiration Alerts - Show only if there are expiring documents */}
      {(expiringDocs.expired_count > 0 || expiringDocs.expiring_soon_count > 0) && (
        <div className="card p-6 border-l-4 border-amber-400">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-gray-900 flex items-center tracking-snug">
              <Calendar className="h-5 w-5 mr-2 text-amber-500" />
              Document Expiration
            </h2>
            <div className="flex items-center gap-2">
              {expiringDocs.expired_count > 0 && (
                <span className="badge-red">{expiringDocs.expired_count} expired</span>
              )}
              {expiringDocs.expiring_soon_count > 0 && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                  {expiringDocs.expiring_soon_count} expiring soon
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {/* Expired documents first */}
            {expiringDocs.expired.slice(0, 3).map((doc) => (
              <button
                key={doc.id}
                onClick={() => navigate('/documents')}
                className="w-full text-left p-3 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 hover:border-red-300 transition-all group"
              >
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:underline truncate">
                      {doc.title}
                    </p>
                    <p className="text-xs text-red-600 font-medium mt-0.5">
                      Expired - Requires immediate attention
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 ml-2" />
                </div>
              </button>
            ))}
            {/* Expiring soon documents */}
            {expiringDocs.expiring_soon.slice(0, 3).map((doc) => (
              <button
                key={doc.id}
                onClick={() => navigate('/documents')}
                className="w-full text-left p-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all group"
              >
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:underline truncate">
                      {doc.title}
                    </p>
                    <p className="text-xs text-amber-600 font-medium mt-0.5">
                      Expires in {doc.days_until_expiration} day{doc.days_until_expiration !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 ml-2" />
                </div>
              </button>
            ))}
            {/* Show "View all" if there are more documents */}
            {(expiringDocs.expired.length > 3 || expiringDocs.expiring_soon.length > 3) && (
              <Link
                to="/documents"
                className="block text-center text-sm text-primary-600 hover:text-primary-700 py-2"
              >
                View all expiring documents <ArrowRight className="h-4 w-4 inline ml-1" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Key Risk Indicators Widget (Issue #127) */}
      {kriSummary && kriSummary.total > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-gray-900 flex items-center tracking-snug">
              <Gauge className="h-5 w-5 mr-2 text-primary-500" />
              Key Risk Indicators
            </h2>
            <Link
              to="/kris"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          {/* KRI Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            <div className="bg-surface-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{kriSummary.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
              <p className="text-2xl font-bold text-green-600">{kriSummary.green}</p>
              <p className="text-xs text-green-600">On Target</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-100">
              <p className="text-2xl font-bold text-yellow-600">{kriSummary.yellow}</p>
              <p className="text-xs text-yellow-600">Warning</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
              <p className="text-2xl font-bold text-red-600">{kriSummary.red}</p>
              <p className="text-xs text-red-600">Critical</p>
            </div>
            <div className="bg-surface-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-400">{kriSummary.no_data}</p>
              <p className="text-xs text-gray-500">No Data</p>
            </div>
          </div>

          {/* Top KRIs requiring attention */}
          {topKris.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">Requires Attention</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {topKris.map((kri) => (
                  <Link
                    key={kri.id}
                    to="/kris"
                    className={`flex items-center p-3 rounded-lg border hover:shadow-sm transition-all ${
                      kri.current_status === 'red'
                        ? 'bg-red-50 border-red-200 hover:border-red-300'
                        : kri.current_status === 'yellow'
                        ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                      kri.current_status === 'red'
                        ? 'bg-red-100'
                        : kri.current_status === 'yellow'
                        ? 'bg-yellow-100'
                        : 'bg-gray-100'
                    }`}>
                      {kri.current_status === 'red' ? (
                        <XCircle className="h-5 w-5 text-red-600" />
                      ) : kri.current_status === 'yellow' ? (
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      ) : (
                        <Activity className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{kri.name}</p>
                      <p className={`text-xs font-medium ${
                        kri.current_status === 'red'
                          ? 'text-red-600'
                          : kri.current_status === 'yellow'
                          ? 'text-yellow-600'
                          : 'text-gray-500'
                      }`}>
                        {kri.current_value != null
                          ? `${kri.current_value}${kri.unit || ''}`
                          : 'No data'}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {topKris.length === 0 && kriSummary.green === kriSummary.total && (
            <div className="text-center py-4">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-green-600 font-medium">All KRIs are on target</p>
            </div>
          )}
        </div>
      )}

      {/* Activity Feed & Upcoming Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Feed */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-gray-900 flex items-center tracking-snug">
              <Activity className="h-5 w-5 mr-2 text-primary-500" />
              Recent Activity
            </h2>
            <Link
              to="/audit-logs"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-3">
            {activity.length > 0 ? (
              activity.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="flex items-start p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200 mr-3">
                    {getActivityIcon(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{item.username || 'User'}</span>{' '}
                      {getActivityDescription(item)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatRelativeTime(item.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Tasks */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-gray-900 flex items-center tracking-snug">
              <Calendar className="h-5 w-5 mr-2 text-primary-500" />
              Upcoming Tasks
            </h2>
            <Link
              to="/tasks"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <div className="space-y-3">
            {data?.upcoming_tasks && data.upcoming_tasks.length > 0 ? (
              data.upcoming_tasks.map((task, index) => {
                const daysUntil = getDaysUntilDue(task.due_date)
                const urgencyClass = getDueUrgencyClass(daysUntil)
                return (
                  <Link
                    key={`${task.id}-${index}`}
                    to={`/controls/${task.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start flex-1 min-w-0 mr-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${urgencyClass}`}>
                        {daysUntil <= 3 ? (
                          <AlertTriangle className="h-4 w-4" />
                        ) : (
                          <Clock className="h-4 w-4" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {task.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {task.description}
                        </p>
                      </div>
                    </div>
                    <div className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium ${urgencyClass}`}>
                      {daysUntil < 0
                        ? `${Math.abs(daysUntil)}d overdue`
                        : daysUntil === 0
                        ? 'Today'
                        : daysUntil === 1
                        ? 'Tomorrow'
                        : `${daysUntil} days`}
                    </div>
                  </Link>
                )
              })
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="text-gray-500">No upcoming tasks</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h2 className="text-lg font-heading font-semibold text-gray-900 mb-4 tracking-snug">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/evidence?action=upload"
            className="flex items-center p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <FileText className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <p className="font-medium text-primary-900">Upload Evidence</p>
              <p className="text-sm text-primary-600">Add compliance documentation</p>
            </div>
          </Link>

          <Link
            to="/controls?status=pending"
            className="flex items-center p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
          >
            <Clock className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <p className="font-medium text-yellow-900">Review Pending</p>
              <p className="text-sm text-yellow-600">
                {data?.controls.pending || 0} controls need review
              </p>
            </div>
          </Link>

          <Link
            to="/controls?status=overdue"
            className="flex items-center p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <p className="font-medium text-red-900">Address Overdue</p>
              <p className="text-sm text-red-600">
                {data?.controls.overdue || 0} controls overdue
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Score Breakdown Modal */}
      <ScoreBreakdownModal
        isOpen={showScoreBreakdown}
        onClose={() => setShowScoreBreakdown(false)}
        data={scoreBreakdownData}
        loading={scoreBreakdownLoading}
        onNavigateToControl={navigateToControl}
      />
    </div>
  )
}
